/**
 * Renders the highest-priority classified runtime error on the RMA timeline, with optional
 * original message lines for system admins (toggle). Message lines are plain text rendered via JSX.
 */
import React, { useState } from "react";
import { IClassifiedError, IRmaError } from "../../rma-types";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import TextTruncate from "../../../../../components/text-truncate/text-truncate";
import "./timeline-classified-error.scss";

interface ClassifiedErrorTimelineProps {
    returnError: IRmaError;
    highestPriorityClassifiedError: IClassifiedError;
    rawContentLines: string[];
    isSuperAdmin: boolean;
}

const TimelineClassifiedError: React.FC<ClassifiedErrorTimelineProps> = ({
    returnError,
    highestPriorityClassifiedError,
    rawContentLines,
    isSuperAdmin,
}) => {
    const [showOriginal, setShowOriginal] = useState(false);

    return (
        <div className="timeline-classified-error">
            <div>
                <FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                    {highestPriorityClassifiedError.message}
                </FontBody1>
            </div>
            {returnError.vendor_label && (
                <div>
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>{`Error from ${returnError.vendor_label}`}</FontBody2>
                </div>
            )}
            <div className="timeline-classified-error-block">
                <FontBody2 color={FONT_COLOR.PRIMARY2}>Issue(s) encountered</FontBody2>
                <ul>
                    <li key={highestPriorityClassifiedError.id}>{returnError.short_error}</li>
                </ul>
            </div>
            <div className="timeline-classified-error-block">
                <FontBody2 color={FONT_COLOR.PRIMARY2}>Recommended action(s)</FontBody2>
                <ul>
                    <li key={highestPriorityClassifiedError.id}>{highestPriorityClassifiedError.recommended_action}</li>
                </ul>
            </div>
            {isSuperAdmin && (
                <div className="timeline-classified-error-admin">
                    {highestPriorityClassifiedError.code && (
                        <FontBody2 color={FONT_COLOR.PRIMARY2}>
                            Error code: {highestPriorityClassifiedError.code}
                        </FontBody2>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowOriginal((v) => !v)}
                    >
                        {(showOriginal ? "Hide original message" : "Show original message") + " (Admin view only)"}
                    </button>
                    {showOriginal && (
                        <TextTruncate allowExpand={true} maxLines={3}>
                            <div className="timeline-classified-error-original-lines">
                                {rawContentLines.map((line, index) => (
                                    <div key={index}>{line}</div>
                                ))}
                            </div>
                        </TextTruncate>
                    )}
                </div>
            )}
        </div>
    );
};

export default TimelineClassifiedError;
